import React, { useState } from 'react';
import { Plus, Trash } from '@phosphor-icons/react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../ui/dialog';

const getTierClassName = (tier) => {
  if (tier === 'elite') return 'bg-[#D4AF37]/20 text-[#D4AF37]';
  if (tier === 'premium') return 'bg-white/10 text-white';
  return 'bg-zinc-800 text-zinc-400';
};

const getActionClassName = (action) => {
  if (action === 'BUY') return 'bg-green-500/20 text-green-500';
  if (action === 'SELL') return 'bg-red-500/20 text-red-500';
  return 'bg-zinc-800 text-zinc-400';
};

const SignalsTab = ({ signals, onCreateSignal, onDeleteSignal }) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [signalForm, setSignalForm] = useState({ 
    symbol: '', action: 'BUY', entry_price: '', target_price: '', stop_loss: '', tier_required: 'basic', analysis: '' 
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    await onCreateSignal({
      ...signalForm,
      entry_price: parseFloat(signalForm.entry_price),
      target_price: signalForm.target_price ? parseFloat(signalForm.target_price) : null,
      stop_loss: signalForm.stop_loss ? parseFloat(signalForm.stop_loss) : null
    });
    setIsDialogOpen(false);
    setSignalForm({ symbol: '', action: 'BUY', entry_price: '', target_price: '', stop_loss: '', tier_required: 'basic', analysis: '' });
  };

  return (
    <>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl text-white font-outfit">Trading Signals</h2>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#D4AF37] text-black hover:bg-[#E5C158]">
              <Plus className="w-4 h-4 mr-2" />
              Add Signal
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#121212] border-white/10">
            <DialogHeader>
              <DialogTitle className="text-white">Add New Signal</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-zinc-400">Symbol</Label>
                  <Input
                    value={signalForm.symbol}
                    onChange={(e) => setSignalForm({ ...signalForm, symbol: e.target.value })}
                    className="bg-zinc-900 border-zinc-700 text-white"
                    placeholder="BTC/USD"
                    required
                  />
                </div>
                <div>
                  <Label className="text-zinc-400">Action</Label>
                  <Select 
                    value={signalForm.action} 
                    onValueChange={(value) => setSignalForm({ ...signalForm, action: value })}
                  >
                    <SelectTrigger className="bg-zinc-900 border-zinc-700 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#121212] border-zinc-700">
                      <SelectItem value="BUY">BUY</SelectItem>
                      <SelectItem value="SELL">SELL</SelectItem>
                      <SelectItem value="HOLD">HOLD</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="text-zinc-400">Entry Price</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={signalForm.entry_price}
                    onChange={(e) => setSignalForm({ ...signalForm, entry_price: e.target.value })}
                    className="bg-zinc-900 border-zinc-700 text-white"
                    required
                  />
                </div>
                <div>
                  <Label className="text-zinc-400">Target</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={signalForm.target_price}
                    onChange={(e) => setSignalForm({ ...signalForm, target_price: e.target.value })}
                    className="bg-zinc-900 border-zinc-700 text-white"
                  />
                </div>
                <div>
                  <Label className="text-zinc-400">Stop Loss</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={signalForm.stop_loss}
                    onChange={(e) => setSignalForm({ ...signalForm, stop_loss: e.target.value })}
                    className="bg-zinc-900 border-zinc-700 text-white"
                  />
                </div>
              </div>
              <div>
                <Label className="text-zinc-400">Required Tier</Label>
                <Select 
                  value={signalForm.tier_required} 
                  onValueChange={(value) => setSignalForm({ ...signalForm, tier_required: value })}
                >
                  <SelectTrigger className="bg-zinc-900 border-zinc-700 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#121212] border-zinc-700">
                    <SelectItem value="basic">Basic</SelectItem>
                    <SelectItem value="premium">Premium</SelectItem>
                    <SelectItem value="elite">Elite</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-zinc-400">Analysis</Label>
                <Textarea
                  value={signalForm.analysis}
                  onChange={(e) => setSignalForm({ ...signalForm, analysis: e.target.value })}
                  className="bg-zinc-900 border-zinc-700 text-white"
                />
              </div>
              <Button type="submit" className="w-full bg-[#D4AF37] text-black hover:bg-[#E5C158]">
                Create Signal
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-[#121212] border border-white/5 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-black/30">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-mono uppercase text-zinc-500">Symbol</th>
                <th className="text-left px-4 py-3 text-xs font-mono uppercase text-zinc-500">Action</th>
                <th className="text-left px-4 py-3 text-xs font-mono uppercase text-zinc-500">Entry</th>
                <th className="text-left px-4 py-3 text-xs font-mono uppercase text-zinc-500">Target</th>
                <th className="text-left px-4 py-3 text-xs font-mono uppercase text-zinc-500">Stop</th>
                <th className="text-left px-4 py-3 text-xs font-mono uppercase text-zinc-500">Tier</th>
                <th className="text-left px-4 py-3 text-xs font-mono uppercase text-zinc-500">Actions</th>
              </tr>
            </thead>
            <tbody>
              {signals.map((signal) => (
                <tr key={signal.signal_id} className="border-t border-white/5">
                  <td className="px-4 py-3 text-white font-mono">{signal.symbol}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-1 ${getActionClassName(signal.action)}`}>
                      {signal.action}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-zinc-400 font-mono">${signal.entry_price}</td>
                  <td className="px-4 py-3 text-green-500 font-mono">{signal.target_price ? `$${signal.target_price}` : '-'}</td>
                  <td className="px-4 py-3 text-red-500 font-mono">{signal.stop_loss ? `$${signal.stop_loss}` : '-'}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-1 ${getTierClassName(signal.tier_required)}`}>
                      {signal.tier_required}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onDeleteSignal(signal.signal_id)}
                      className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                    >
                      <Trash className="w-4 h-4" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

export default SignalsTab;
